/**
 * The agent side panel's Variables tab.
 *
 * The job's variable tree, drawn with the same <Tree> the Tables tab uses, so
 * the two tabs cannot drift — search, tooltips and tick behaviour included.
 * Ticking a variable chips the composer; it never sends. The tick is a mirror
 * of the chips, so unticking removes the chip.
 *
 * 🔴 The tick state comes from the CHIPS, not from useVariableTree's own
 * `checked`/`getCheckboxState`. That hook carries a full selection model of its
 * own (used by the dashboard editors), and adopting it here would leave two
 * sources of truth for "is this attached" that could disagree. The hook is used
 * for tree data, expansion and code loading only.
 *
 * Folders and code frames are not tickable: a chip names one variable, and the
 * agent needs a variable name to do anything with it.
 *
 * Job picker: mirrors the Tables tab (8 Sep, Matt), so a session can reference
 * variables from a job other than its own — restricted to `sessionJobs` (T18),
 * same rule as AgentTocTab: an unselected job cannot be reached from here.
 */
import React, { useMemo, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'

import Tree from '../tree/Tree'
import { GenNode } from '../../types/carbon/models/GenNode'
import { buildVariableChip, type ComposerChip } from '../../hooks/useComposerChips'
import useVariableTree from '../../hooks/useVariableTree'
import useAccount from '../../hooks/useAccount'
import TreePreviewAction from './TreePreviewAction'
import { DashboardJobsProvider, DashboardJobSelect, type DashboardJobOption } from '../dashboard/DashboardJobs'

export interface AgentVariablesTabProps {
  customer: string
  /** The session's job. Empty until the session has resolved. */
  job: string
  /**
   * The session's selected job set (T18). The job dropdown offers only these —
   * a job outside this list cannot be reached from it, and the default
   * selection is its first entry.
   */
  sessionJobs: string[]
  /** Ids of the variables currently chipped, so the tree can mirror them. */
  selectedIds: Set<string>
  /** Add (on=true) or remove (on=false) a variable's chip. */
  onToggle: (chip: ComposerChip, on: boolean) => void
  /** Show the variable's codes without attaching it. Clicking its NAME does this. */
  onPreview?: (chip: ComposerChip) => void
}

export default function AgentVariablesTab({ customer, job, sessionJobs, selectedIds, onToggle, onPreview }: AgentVariablesTabProps) {
  const [treeSelected, setTreeSelected] = useState<string>('')
  // Same job-first pattern as the Tables tab: pick a job, but only a SELECTED
  // one sticks — see AgentTocTab for why this needs no effect.
  const [pickJob, setPickJob] = useState('')
  const treeJob = (pickJob && sessionJobs.includes(pickJob)) ? pickJob : (sessionJobs[0] || job)

  const account = useAccount()
  const jobOptions: DashboardJobOption[] = useMemo(() => {
    const sessionCust = account.user?.session_info?.sessionCusts?.find(c => c.name === customer)
    return (sessionCust?.sessionJobs || [])
      .filter((j): j is { name: string; displayName?: string | null } => Boolean(j.name))
      .filter(j => sessionJobs.includes(j.name))
      .map(j => ({ name: j.name, displayName: j.displayName || undefined }))
  }, [account.user, customer, sessionJobs])

  // Codes are loaded lazily, on expand — the tree can be thousands of
  // variables and nothing here needs their codes to build a chip.
  const variableTree = useVariableTree({
    customer,
    job: treeJob,
    loadCodes: false,
  })

  const jobLabel = jobOptions.find(j => j.name === treeJob)?.displayName
  const chipFor = (node: GenNode): ComposerChip => buildVariableChip({
    jobLabel,
    name: node.value1 || '',
    folderPath: node.value2 || '',
    job: treeJob,
    // The question text the vartree carries alongside the name. It is what
    // makes a variable recognisable, but it is a sentence — so it lands in the
    // chip's hover text, not on its face.
    description: node.meta?.find(m => m.key === 'question')?.value || undefined,
  })

  const handleNodeSelectToggle = (itemId: string, node: GenNode, isNodeSelected: boolean) => {
    if (!isNodeSelected) return
    setTreeSelected(itemId)
    if (node.type !== 'Variable') return
    // The NAME shows the variable's codes; the TICK attaches it. Same split as
    // the Tables tab — looking is never the same gesture as committing.
    onPreview?.(chipFor(node))
  }

  const handleNodeCheckedToggle = (_itemId: string, node: GenNode, isChecked: boolean) => {
    if (node.type !== 'Variable') return
    onToggle(chipFor(node), isChecked)
  }

  const body = () => {
    if (!treeJob) {
      return (
        <Typography sx={{ fontSize: 13, color: 'text.secondary', px: 1 }}>
          This session has no job, so there are no variables to show.
        </Typography>
      )
    }
    if (variableTree.isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={22} />
        </Box>
      )
    }
    if (variableTree.isError) {
      return (
        <Typography sx={{ fontSize: 13, color: 'error.main', px: 1 }}>
          Could not load the variables for this job.
        </Typography>
      )
    }
    if (!variableTree.nodes || variableTree.nodes.length === 0) {
      return (
        <Typography sx={{ fontSize: 13, color: 'text.secondary', px: 1 }}>
          No variables found for this job.
        </Typography>
      )
    }
    return (
      <Tree
        searchEnabled
        searchPlaceholder="Search variables..."
        nodes={variableTree.nodes}
        expanded={variableTree.expanded}
        selected={treeSelected}
        getCheckboxState={node => node.type === 'Variable'
          ? { enabled: true, checked: selectedIds.has(chipFor(node).id) }
          : { enabled: false, checked: false }}
        getNodeTooltip={node => node.type === 'Variable' ? chipFor(node).tooltip : undefined}
        getNodeActions={node => node.type === 'Variable' && onPreview
          ? <TreePreviewAction label={chipFor(node).label} onPreview={() => onPreview(chipFor(node))} />
          : null}
        onNodeSelectToggle={handleNodeSelectToggle}
        onNodeCheckedToggle={handleNodeCheckedToggle}
        onNodeExpandToggle={variableTree.onNodeExpandToggle}
      />
    )
  }

  return (
    <DashboardJobsProvider value={{ jobs: jobOptions, primaryJob: job }}>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 1, overflow: 'hidden', gap: 1 }}>
        <DashboardJobSelect
          value={treeJob}
          ariaLabel="Variable job"
          onChange={(j) => { setPickJob(j); setTreeSelected('') }}
        />
        {body()}
      </Box>
    </DashboardJobsProvider>
  )
}
